import { useState, useRef, useCallback, useEffect } from "react";
import {
  LayoutGrid, Plus, Edit2, Trash2, X, Check,
  RefreshCw, Move, Circle, Square, Maximize2, Settings2,
  ChevronDown, Users,
} from "lucide-react";
import { useAdmin } from "../admin-context";

// ── Constants ─────────────────────────────────────────────────────────────────
const SVG_W = 900;
const SVG_H = 520;

const SHAPE_DEFAULTS = {
  circle:    { w: 80,  h: 80  },
  rect:      { w: 100, h: 70  },
  rectangle: { w: 130, h: 70  },
};

const STATUS_COLORS = {
  available: { fill: "#22c55e", chair: "#16a34a", text: "#fff" },
  reserved:  { fill: "#f59e0b", chair: "#d97706", text: "#fff" },
  occupied:  { fill: "#ef4444", chair: "#dc2626", text: "#fff" },
  inactive:  { fill: "#cbd5e1", chair: "#94a3b8", text: "#64748b" },
};

const STATUS_OPTS = ["available", "reserved", "occupied", "inactive"];

const SECTION_COLORS = [
  "#f97316", "#3b82f6", "#8b5cf6", "#10b981", "#f43f5e", "#0ea5e9",
];

// ── Reusable UI ───────────────────────────────────────────────────────────────
const Drawer = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative bg-white h-full ${wide ? "max-w-lg" : "max-w-md"} w-full shadow-2xl flex flex-col overflow-hidden animate-in slide-in-from-right duration-200`}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

const FF = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inp = "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent";
const sel = inp + " bg-white";

const Badge = ({ status }) => {
  const map = {
    available: "bg-emerald-100 text-emerald-700",
    reserved:  "bg-amber-100 text-amber-700",
    occupied:  "bg-red-100 text-red-700",
    inactive:  "bg-gray-100 text-gray-500",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${map[status] || "bg-gray-100 text-gray-500"}`}>
      {status}
    </span>
  );
};

// ── SVG Table Shapes ──────────────────────────────────────────────────────────
const SvgCircleTable = ({ table, selected, onMouseDown }) => {
  const cx = table.pos_x + 40;
  const cy = table.pos_y + 40;
  const r  = 34;
  const c  = STATUS_COLORS[table.status] || STATUS_COLORS.available;
  const chairR  = 9;
  const armDist = r + chairR + 5;
  const chairs  = [[cx, cy - armDist],[cx + armDist, cy],[cx, cy + armDist],[cx - armDist, cy]];
  return (
    <g
      style={{ cursor: "grab" }}
      onMouseDown={(e) => onMouseDown(e, table)}
    >
      {selected && <circle cx={cx} cy={cy} r={r + 10} fill="none" stroke="#f97316" strokeWidth={3} opacity={0.6} />}
      {chairs.map(([x, y], i) => <circle key={i} cx={x} cy={y} r={chairR} fill={c.chair} />)}
      <circle cx={cx} cy={cy} r={r} fill={c.fill} />
      <text x={cx} y={cy - 4} textAnchor="middle" fill={c.text} fontSize={11} fontWeight="bold" fontFamily="sans-serif">{table.name}</text>
      <text x={cx} y={cy + 9} textAnchor="middle" fill={c.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">{table.capacity} pax</text>
    </g>
  );
};

const SvgRectTable = ({ table, selected, onMouseDown }) => {
  const isLong = table.shape === "rectangle";
  const w  = isLong ? 128 : 100;
  const h  = 68;
  const x  = table.pos_x;
  const y  = table.pos_y;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const c  = STATUS_COLORS[table.status] || STATUS_COLORS.available;
  const chairs = isLong
    ? [[cx - 32, y - 11],[cx, y - 11],[cx + 32, y - 11],[cx - 32, y + h + 2],[cx, y + h + 2],[cx + 32, y + h + 2]]
    : [[cx - 20, y - 11],[cx + 20, y - 11],[cx - 20, y + h + 2],[cx + 20, y + h + 2]];
  return (
    <g
      style={{ cursor: "grab" }}
      onMouseDown={(e) => onMouseDown(e, table)}
    >
      {selected && <rect x={x - 6} y={y - 6} width={w + 12} height={h + 12} rx={14} fill="none" stroke="#f97316" strokeWidth={3} opacity={0.6} />}
      {chairs.map(([cx2, cy2], i) => <ellipse key={i} cx={cx2} cy={cy2} rx={9} ry={7} fill={c.chair} />)}
      <rect x={x} y={y} width={w} height={h} rx={8} fill={c.fill} />
      <text x={cx} y={cy - 3} textAnchor="middle" fill={c.text} fontSize={11} fontWeight="bold" fontFamily="sans-serif">{table.name}</text>
      <text x={cx} y={cy + 11} textAnchor="middle" fill={c.text} fontSize={9} opacity={0.85} fontFamily="sans-serif">{table.capacity} pax</text>
    </g>
  );
};

const SvgTable = ({ table, selected, onMouseDown }) =>
  table.shape === "circle"
    ? <SvgCircleTable table={table} selected={selected} onMouseDown={onMouseDown} />
    : <SvgRectTable   table={table} selected={selected} onMouseDown={onMouseDown} />;

// ── Main Page ─────────────────────────────────────────────────────────────────
const TablesPage = () => {
  const { tables, loadTables, addTable, updateTable, deleteTable, showToast } = useAdmin();

  const [tab,        setTab]        = useState("floorplan"); // floorplan | list
  const [selected,   setSelected]   = useState(null);
  const [dragging,   setDragging]   = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [deleteId,   setDeleteId]   = useState(null);
  const [filter,     setFilter]     = useState("all");

  const svgRef = useRef(null);

  const emptyForm = {
    name: "", shape: "circle", capacity: 2,
    section: "", status: "available", pos_x: 100, pos_y: 100,
  };
  const [form, setForm] = useState(emptyForm);

  useEffect(() => { loadTables(); }, []);

  // ── Sections derived from table data ─────────────────────────────────────
  const sections = [...new Set((tables || []).map(t => t.section).filter(Boolean))];

  // ── Filtered tables ───────────────────────────────────────────────────────
  const displayed = (tables || []).filter(t => {
    if (filter === "all") return true;
    if (STATUS_OPTS.includes(filter)) return t.status === filter;
    return t.section === filter;
  });

  // ── Drawer helpers ────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDrawerOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, shape: t.shape || "circle", capacity: t.capacity,
      section: t.section || "", status: t.status || "available",
      pos_x: t.pos_x ?? 100, pos_y: t.pos_y ?? 100,
    });
    setDrawerOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return showToast("Table name is required", "error");
    if (!form.capacity || form.capacity < 1) return showToast("Capacity must be at least 1", "error");
    setSaving(true);
    try {
      if (editing) {
        await updateTable(editing.id, { ...form, license_id: 2 });
        showToast("Table updated");
      } else {
        await addTable({ ...form, license_id: 2 });
        showToast("Table added");
      }
      setDrawerOpen(false);
    } catch {
      showToast("Failed to save table", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteTable(id);
      setDeleteId(null);
      setSelected(null);
      showToast("Table deleted");
    } catch {
      showToast("Failed to delete", "error");
    }
  };

  // ── Drag logic ────────────────────────────────────────────────────────────
  const svgPoint = useCallback((e) => {
    const svg = svgRef.current;
    if (!svg) return { x: 0, y: 0 };
    const rect = svg.getBoundingClientRect();
    const sx   = SVG_W / rect.width;
    const sy   = SVG_H / rect.height;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  }, []);

  const onTableMouseDown = useCallback((e, table) => {
    e.stopPropagation();
    setSelected(table.id);
    const pt = svgPoint(e);
    setDragging(table);
    setDragOffset({ x: pt.x - (table.pos_x ?? 0), y: pt.y - (table.pos_y ?? 0) });
  }, [svgPoint]);

  const onSvgMouseMove = useCallback((e) => {
    if (!dragging) return;
    const pt = svgPoint(e);
    const nx = Math.max(0, Math.min(SVG_W - 140, pt.x - dragOffset.x));
    const ny = Math.max(0, Math.min(SVG_H - 100, pt.y - dragOffset.y));
    // optimistic local move
    setDragging(prev => ({ ...prev, pos_x: nx, pos_y: ny }));
  }, [dragging, dragOffset, svgPoint]);

  const onSvgMouseUp = useCallback(async () => {
    if (!dragging) return;
    const { id, ...rest } = dragging;
    try {
      await updateTable(id, { ...rest, pos_x: Math.round(dragging.pos_x), pos_y: Math.round(dragging.pos_y) });
    } catch {
      showToast("Failed to save position", "error");
    }
    setDragging(null);
  }, [dragging, updateTable, showToast]);

  // Merge optimistic drag position into display list
  const displayTables = (tables || []).map(t =>
    dragging && t.id === dragging.id ? { ...t, pos_x: dragging.pos_x, pos_y: dragging.pos_y } : t
  );

  // ── Stats ─────────────────────────────────────────────────────────────────
  const stats = STATUS_OPTS.map(s => ({
    label: s,
    count: (tables || []).filter(t => t.status === s).length,
  }));

  const statColors = {
    available: "text-emerald-600 bg-emerald-50",
    reserved:  "text-amber-600 bg-amber-50",
    occupied:  "text-red-600 bg-red-50",
    inactive:  "text-gray-500 bg-gray-50",
  };

  return (
    <div className="p-6 space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900">Table Management</h1>
          <p className="text-sm text-gray-500 mt-0.5">Design your floor plan and manage table availability</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => loadTables()} className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 transition">
            <RefreshCw size={16} className="text-gray-500" />
          </button>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold text-sm transition"
          >
            <Plus size={16} /> Add Table
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(({ label, count }) => (
          <div key={label} className={`rounded-2xl p-4 ${statColors[label]}`}>
            <p className="text-2xl font-extrabold">{count}</p>
            <p className="text-xs font-semibold capitalize mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-xl w-fit">
        {[["floorplan","Floor Plan"],["list","List View"]].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === v ? "bg-white shadow text-gray-900" : "text-gray-500 hover:text-gray-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2">
        {["all", ...STATUS_OPTS, ...sections].map(f => (
          <button key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition capitalize
              ${filter === f ? "bg-orange-500 text-white border-orange-500" : "bg-white text-gray-600 border-gray-200 hover:border-orange-300"}`}>
            {f}
          </button>
        ))}
      </div>

      {/* ── Floor Plan ── */}
      {tab === "floorplan" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">
              <Move size={14} className="inline mr-1.5 text-gray-400" />
              Drag tables to reposition
            </p>
            <p className="text-xs text-gray-400">{(tables||[]).length} tables total</p>
          </div>
          <svg
            ref={svgRef}
            viewBox={`0 0 ${SVG_W} ${SVG_H}`}
            className="w-full"
            style={{ background: "#f8fafc", userSelect: "none" }}
            onMouseMove={onSvgMouseMove}
            onMouseUp={onSvgMouseUp}
            onMouseLeave={onSvgMouseUp}
            onClick={() => setSelected(null)}
          >
            {/* Grid */}
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width={SVG_W} height={SVG_H} fill="url(#grid)" />

            {displayTables.filter(t => filter === "all" || t.status === filter || t.section === filter).map(t => (
              <SvgTable key={t.id} table={t} selected={selected === t.id} onMouseDown={onTableMouseDown} />
            ))}
          </svg>

          {/* Selected table actions */}
          {selected && (() => {
            const t = (tables||[]).find(x => x.id === selected);
            if (!t) return null;
            return (
              <div className="flex items-center gap-3 px-5 py-3 bg-orange-50 border-t border-orange-100">
                <p className="font-semibold text-gray-800 text-sm flex-1">{t.name} — <Badge status={t.status} /></p>
                <button onClick={() => openEdit(t)} className="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-semibold rounded-lg hover:bg-gray-50 flex items-center gap-1">
                  <Edit2 size={12} /> Edit
                </button>
                <button onClick={() => setDeleteId(t.id)} className="px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 text-xs font-semibold rounded-lg hover:bg-red-100 flex items-center gap-1">
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            );
          })()}
        </div>
      )}

      {/* ── List View ── */}
      {tab === "list" && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {["Name","Shape","Capacity","Section","Status","Actions"].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayed.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400 text-sm">No tables found</td></tr>
              )}
              {displayed.map(t => (
                <tr key={t.id} className="hover:bg-gray-50 transition">
                  <td className="px-4 py-3 font-semibold text-gray-900">{t.name}</td>
                  <td className="px-4 py-3 capitalize text-gray-600">{t.shape || "circle"}</td>
                  <td className="px-4 py-3 text-gray-600">
                    <span className="flex items-center gap-1"><Users size={13} className="text-gray-400" />{t.capacity}</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500">{t.section || "—"}</td>
                  <td className="px-4 py-3"><Badge status={t.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(t)} className="p-1.5 hover:bg-orange-50 rounded-lg transition">
                        <Edit2 size={14} className="text-orange-500" />
                      </button>
                      <button onClick={() => setDeleteId(t.id)} className="p-1.5 hover:bg-red-50 rounded-lg transition">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Add/Edit Drawer ── */}
      <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title={editing ? "Edit Table" : "Add Table"}>
        <div className="space-y-4">
          <FF label="Table Name" required>
            <input className={inp} value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="e.g. T-01, Window Seat" />
          </FF>

          <FF label="Shape">
            <div className="grid grid-cols-3 gap-2">
              {[["circle","Circle"],["rect","Square"],["rectangle","Rectangle"]].map(([v,l]) => (
                <button key={v} onClick={() => setForm(f => ({...f, shape: v}))}
                  className={`flex flex-col items-center py-3 rounded-xl border-2 text-xs font-semibold transition
                    ${form.shape === v ? "border-orange-400 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-500 hover:border-gray-300"}`}>
                  {v === "circle" ? <Circle size={20} className="mb-1" /> : <Square size={20} className="mb-1" />}
                  {l}
                </button>
              ))}
            </div>
          </FF>

          <div className="grid grid-cols-2 gap-3">
            <FF label="Capacity (pax)" required>
              <input type="number" min={1} max={20} className={inp} value={form.capacity}
                onChange={e => setForm(f => ({...f, capacity: Number(e.target.value)}))} />
            </FF>
            <FF label="Status">
              <select className={sel} value={form.status} onChange={e => setForm(f => ({...f, status: e.target.value}))}>
                {STATUS_OPTS.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </FF>
          </div>

          <FF label="Section / Zone">
            <input className={inp} value={form.section}
              onChange={e => setForm(f => ({...f, section: e.target.value}))}
              placeholder="e.g. Indoor, Terrace, Private" />
          </FF>

          <FF label="Position on Floor Plan">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-400 mb-1 block">X</label>
                <input type="number" className={inp} value={form.pos_x}
                  onChange={e => setForm(f => ({...f, pos_x: Number(e.target.value)}))} />
              </div>
              <div>
                <label className="text-xs text-gray-400 mb-1 block">Y</label>
                <input type="number" className={inp} value={form.pos_y}
                  onChange={e => setForm(f => ({...f, pos_y: Number(e.target.value)}))} />
              </div>
            </div>
          </FF>

          <div className="flex gap-3 pt-2">
            <button onClick={() => setDrawerOpen(false)} className="flex-1 py-2.5 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50 transition">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex-1 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold transition flex items-center justify-center gap-2">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {editing ? "Save Changes" : "Add Table"}
            </button>
          </div>
        </div>
      </Drawer>

      {/* ── Delete Confirm ── */}
      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-80">
            <h3 className="font-bold text-gray-900 mb-2">Delete Table?</h3>
            <p className="text-sm text-gray-500 mb-5">This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2 border border-gray-200 rounded-xl text-sm font-semibold text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={() => handleDelete(deleteId)} className="flex-1 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl text-sm font-semibold">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TablesPage;
